import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@akuna/ui";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { useClinicAllergies, useClinicTemperaments, type Condition } from "@/hooks/useLookups";
import {
  useCreateClinicCondition,
  useDeleteClinicCondition,
  useUpdateClinicCondition,
} from "@/hooks/useClinicConditionCatalogMutations";
import { useProfile } from "@/hooks/useProfile";
import { toast } from "sonner";

type CatalogKind = "allergy" | "temperament";

function getPostgresErrorCode(err: unknown): string | null {
  if (typeof err !== "object" || err === null) return null;
  if (!("code" in err)) return null;
  const code = (err as { code?: unknown }).code;
  return typeof code === "string" ? code : null;
}

function CatalogSection({
  title,
  items,
  disabled,
  onAdd,
  onEdit,
  onDeleteRequest,
}: {
  title: string;
  items: Condition[];
  disabled: boolean;
  onAdd: () => void;
  onEdit: (row: Condition) => void;
  onDeleteRequest: (row: Condition) => void;
}) {
  const { t } = useTranslation("settings");

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium">{title}</p>
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onAdd}>
          <Plus className="mr-1 h-4 w-4" aria-hidden />
          {t("conditionCatalogAdd")}
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("conditionCatalogEmpty")}</p>
      ) : (
        <ul className="divide-y rounded-md border">
          {items.map((row) => (
            <li
              key={row.id}
              className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
            >
              <span className="min-w-0 truncate">{row.name}</span>
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={disabled}
                  onClick={() => onEdit(row)}
                  aria-label={t("conditionCatalogEdit")}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive"
                  disabled={disabled}
                  onClick={() => onDeleteRequest(row)}
                  aria-label={t("conditionCatalogDelete")}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ClinicConditionCatalogSettings() {
  const { t } = useTranslation("settings");
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  const { data: allergies = [], isLoading: loadingAllergies } = useClinicAllergies();
  const { data: temperaments = [], isLoading: loadingTemperaments } = useClinicTemperaments();

  const createMutation = useCreateClinicCondition();
  const updateMutation = useUpdateClinicCondition();
  const deleteMutation = useDeleteClinicCondition();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogKind, setDialogKind] = useState<CatalogKind>("allergy");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState("");

  const [deleteTarget, setDeleteTarget] = useState<Condition | null>(null);

  const disabled = !clinicId;
  const busy =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const openCreate = (kind: CatalogKind) => {
    setDialogKind(kind);
    setEditingId(null);
    setNameDraft("");
    setDialogOpen(true);
  };

  const openEdit = (kind: CatalogKind, row: Condition) => {
    setDialogKind(kind);
    setEditingId(row.id);
    setNameDraft(row.name ?? "");
    setDialogOpen(true);
  };

  const handleSaveDialog = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      toast.error(t("conditionCatalogNameRequired"));
      return;
    }
    try {
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, name: trimmed });
        toast.success(t("conditionCatalogUpdated"));
      } else {
        await createMutation.mutateAsync({ type: dialogKind, name: trimmed });
        toast.success(t("conditionCatalogCreated"));
      }
      setDialogOpen(false);
    } catch (err: unknown) {
      const code = getPostgresErrorCode(err);
      if (code === "23505") {
        toast.error(t("conditionCatalogDuplicateError"));
      } else {
        toast.error(t("conditionCatalogGenericError"));
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync({ id: deleteTarget.id });
      toast.success(t("conditionCatalogDeleted"));
      setDeleteTarget(null);
    } catch (err: unknown) {
      const code = getPostgresErrorCode(err);
      if (code === "23503") {
        toast.error(t("conditionCatalogDeleteInUse"));
      } else {
        toast.error(t("conditionCatalogDeleteError"));
      }
    }
  };

  return (
    <>
      <div className="grid gap-8 md:grid-cols-2">
        <CatalogSection
          title={t("conditionCatalogAllergiesTitle")}
          items={allergies}
          disabled={disabled || busy || loadingAllergies}
          onAdd={() => openCreate("allergy")}
          onEdit={(row) => openEdit("allergy", row)}
          onDeleteRequest={setDeleteTarget}
        />
        <CatalogSection
          title={t("conditionCatalogTemperamentsTitle")}
          items={temperaments}
          disabled={disabled || busy || loadingTemperaments}
          onAdd={() => openCreate("temperament")}
          onEdit={(row) => openEdit("temperament", row)}
          onDeleteRequest={setDeleteTarget}
        />
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingId ? t("conditionCatalogEditTitle") : t("conditionCatalogCreateTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="condition-catalog-name">{t("conditionCatalogNameLabel")}</Label>
            <Input
              id="condition-catalog-name"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              placeholder={t("conditionCatalogNamePlaceholder")}
              disabled={busy}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleSaveDialog();
              }}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={busy}>
              {t("conditionCatalogCancel")}
            </Button>
            <Button type="button" onClick={() => void handleSaveDialog()} disabled={busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("conditionCatalogSave")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("conditionCatalogDeleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("conditionCatalogDeleteConfirmDescription", {
                name: deleteTarget?.name ?? "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{t("conditionCatalogCancel")}</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={busy}
              onClick={() => void handleConfirmDelete()}
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t("conditionCatalogDelete")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
